import { Filter } from './filter';

export namespace HotelOffers {
  export interface Response {
    program: string;
    version: string;
    release: string;
    datetime: string;
    timestamp: number;
    status: string;
    code: number;
    message: string;
    data: {
      filter: Filter;
      groups: Group[];
      locale?: string;
      expirationTime?: string;
    };
  }

  export interface Filter {
    options: OptionsLocalized[];
    bedTypes: string[];
  }

  export interface OptionsLocalized {
    origin: string;
    localized?: string;
  }

  export interface Group {
    name: string;
    offers: Offer[];
    images: string[];
    publicPrice: PublicPrice[];
  }

  export interface PublicPrice {
    url: string;
    price: number;
    name: PublicPriceSource;
  }

  export enum PublicPriceTags {
    STAY = 'stay',
    SOLDOUT = 'soldout',
  }

  export enum PublicPriceSource {
    BOOKING = 'booking',
    EXPEDIA = 'expedia',
  }

  export interface Offer {
    _roomId: string;
    _rateId: string;
    _product: object;
    _service: number;
    category: string;
    originalName: string;
    freeCancellation: boolean;
    freeCancellationUntil: string;
    filterRef: number[];
    breakfastIncluded: boolean;
    size: number;
    sizeFt: number;
    description: string;
    bed: string;
    flags: string[];
    paymentType: string;
    currency: string;
    billingCurrency: string;
    price: Price;
    priceWithCredits?: Price;
    images: string[];
    bookPayload: boolean;
    cancellationPolicies: CancellationPolicies[];
    paymentSchedule: PaymentSchedule[];
    orderFlow?: OrderFlow;
    redirectUrl?: string;
    view: any;
    merchant?: any;
    id: string;
    loading: boolean;
    soldout: boolean;
    muted?: boolean;
    cancelledBy?: string;
    localizedOptions: OptionsLocalized[];
    policyCancellationType?: HotelOffers.PolicyCancellationType;
    adults?: number;
    children?: number[];
    availableRooms?: number;
  }

  export interface Policy extends Offer {
    expirationTime?: string;
  }

  export interface Policy extends Offer {
    expirationTime?: string;
  }

  export enum OrderFlow {
    bookingPayAtProperty = 'booking pay at property',
    bookingRedirect = 'booking redirect',
    expediaPayAtPropertySimple = 'expedia pay at property simple',
    expediaPayAtProperty2fa = 'expedia pay at property 2fa',
    expediaPayNow = 'expedia pay now',
    tbd = 'TBD',
    notSupported = 'not supported',
  }

  export interface Price {
    base: number;
    taxes: number;
    extraCharges: ExtraCharge[];
    extraChargesSum: number;
    price: number;
    pricePerNight: number;
    payNow?: number;
    payAtProperty?: number;
    effectivePrice: number;
    strikethrough: number;
    save: number;
    savePercent: number;
    cashback: number;
    cashbackPercent: number;
    provider: string;
    creditsToUse?: number;
    _token: string;
    billingExtraCharges?: ExtraCharge[];
    billingExtraChargesSum?: number;
    billingPayNow?: number;
    billingPayAtProperty?: number;
  }

  export interface ExtraCharge {
    name: string;
    amount: number;
  }

  export interface BookPayload {
    booker: Booker;
    currency: string;
    accommodation: Accommodation;
  }

  export interface Booker {
    country: string;
    platform: string;
  }

  export interface Accommodation {
    id: number;
    checkin: string;
    checkout: string;
    products: Product[];
  }

  export interface Product {
    id: string;
    allocation: Allocation;
  }

  export interface Allocation {
    number_of_adults: number;
  }

  export enum FilterKeys {
    BreakfastIncluded = 'breakfast included',
    FreeCancellation = 'free cancellation',
    PayNow = 'pay now',
    PayLater = 'pay later',
    EligibleForPoints = 'eligible for points',
    PayAtProperty = 'pay at property',
    PartiallyRefundable = 'partially refundable',
    // ELIGIBLE_FOR_POINTS = 5,
    // PARTIALLY_REFUNDABLE = 10,
    NonRefundable = 'non refundable',
  }

  export interface BedPriority {
    name: string;
    priority: number[];
  }

  export interface FilterSection {
    header: string;
    options: FilterKeys[];
  }

  export class FilterSectionModel {
    header: string;
    options: Filter.Option[];

    constructor(header: string, options: Filter.Option[]) {
      this.header = header;
      this.options = options;
    }

    static fromFilterSection(section: FilterSection): FilterSectionModel {
      return new FilterSectionModel(
        section.header,
        section.options.map(
          it =>
            new Filter.Option({
              id: it,
              name: it,
            }),
        ),
      );
    }

    clear() {
      this.options.forEach(it => (it.selected = false));
    }
  }

  export function paymentType(offer: Offer): FilterKeys {
    if (offer.flags.includes(FilterKeys.PayNow)) {
      return FilterKeys.PayNow;
    }
    if (offer.flags.includes(FilterKeys.PayLater)) {
      return FilterKeys.PayLater;
    }

    return FilterKeys.PayAtProperty;
  }

  export namespace OffersTrim {
    export const KEY = 'hotel_offers_trimming';

    export function isTrimming(): boolean {
      const stored = localStorage.getItem(KEY);

      try {
        if (stored == null) {
          throw new Error();
        }

        return !!JSON.parse(stored);
      } catch (e) {
        return true;
      }
    }

    export function store(value: boolean) {
      localStorage.setItem(KEY, value ? '1' : '0');
    }
  }

  export interface Review {
    category_id: number;
    name: string;
    count: number;
    score: number;
    type: string;
    color: string;
    default_color: string;
    opacity: number;
  }

  export interface Media {
    bgcolor?: string;
    thumbnail?: string;
    images: string[];
  }

  export interface SearchProps {
    minPrice: string;
    searchFinish: string;
  }

  export interface Location {
    lat: number;
    lon: number;
    district?: string;
    distance_from?: string;
    distance?: number;
    country: string;
    country_name?: string;
    state: string;
    city: string;
    city_id?: number;
    zip?: string;
    address?: string;
  }

  export const OPTION_FILTERS_HEADERS = [
    'options',
    'opcje',
    'opzioni',
    'opciók',
    'možnosti',
  ];

  // export interface BookingPolicy {
  //   result: {
  //     refundable: boolean;
  //     deposit_required: boolean;
  //     deposit_info?: string;
  //     cancellation_info?: string;
  //     currency: string;
  //     deposit_policies?: HotelRoomPolicyFine[];
  //     cancellation_policies?: HotelRoomPolicyFine[];
  //     is_3ds: boolean;
  //     payment_schedule?: PaymentSchedule[];
  //     is_3ds_on_prepay: boolean;
  //     supplier_meta_data?: any;
  //   };
  // }

  export enum PaymentScheduleTypes {
    PayNow = 'pay_now',
    PayAtProperty = 'pay_at_property',
  }

  export interface PaymentSchedule {
    to: string;
    price: number;
    billingPrice: number;
    currency?: string;
    flags?: string;
  }

  export interface HotelRoomPolicyFine {
    from: string;
    price: number;
    billable_price: number;
    billable_currency: string;
  }

  export interface CancellationPolicies {
    billingPrice?: number;
    currency: string;
    from?: string;
    price: number;
  }

  export interface HotelRoomPolicies {
    free?: string;
    fault?: string;
    fine: HotelRoomPolicyFine[];
  }

  export interface HotelRoomPolicyFine {
    currency: string;
    from: string;
    to: string;
    amount: number;
  }

  export interface Policies {
    checkin: PolicyCheckin;
    fees: PolicyFees;
    know_before_you_go: string;
  }

  export interface PolicyCheckin {
    instructions: string;
    min_age: number;
    special_instructions: string;
  }

  export interface PolicyFees {
    mandatory?: string;
    optional?: string;
  }

  export enum BookingGenius {
    UNKNOWN = '-1',
    UNAVAILABLE = '0',
    AVAILABLE = '1',
  }

  export class OfferFilter {
    id?: number | string;
    value_display: string;
  }

  export interface Rating {
    count: number;
    max: number;
    title: string;
  }

  export class ConfirmLabel {
    isDivider: boolean;
    left: Label;
    right: Label;
    ref: string;

    constructor(
      left?: Label,
      right?: Label,
      isDivider?: boolean,
      ref?: string,
    ) {
      this.isDivider = isDivider || false;
      this.left = left || new Label('');
      this.right = right || new Label('');
      this.ref = ref || null;
    }
  }

  export class Label {
    value: string;
    isBold: boolean = false;
    color: string;

    constructor(value: string, isBold?: boolean, color?: string) {
      this.value = value;
      this.isBold = isBold || false;
      this.color = color;
    }
  }

  export interface SupplierLinkModel {
    link: string;
    showWindow: boolean;
    supplier: PublicPriceSource;
    isGenius: boolean;
    title: string;
    description: string;
    btn: string;
  }

  export enum PolicyCancellationType {
    FreeCancellation = 'free cancellation',
    NonRefundable = 'non refundable',
    PartiallyRefundable = 'partially refundable',
    Unknown = 'unknown',
  }
}
